import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getTests, deleteTest, startTestingMode, publishTest, unpublishTest, toggleTestActive } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AdminSideNav } from "@/pages/admin/AdminDashboardPage";
import BrandedHeader from "@/components/BrandedHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Brain, Plus, Pencil, Trash2, Loader2, FileText, Eye, Rocket, RotateCcw, Users, Play, Power, PowerOff, Link2, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

const statusConfig = {
  draft: { color: "bg-secondary text-muted-foreground", label: "Draft" },
  testing: { color: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/25", label: "Testing" },
  published: { color: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border border-[hsl(var(--success))]/25", label: "Published" },
};

export default function AdminTestsPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedTestId, setCopiedTestId] = useState(null);

  useEffect(() => { loadTests(); }, []);

  const loadTests = async () => {
    try {
      const res = await getTests();
      setTests(res.data);
    } catch (e) { toast.error("Failed to load tests"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this test and all its candidates?")) return;
    try { await deleteTest(id); toast.success("Test deleted"); loadTests(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const handleToggleActive = async (test) => {
    try {
      await toggleTestActive(test.id);
      toast.success(`Test ${test.is_active ? 'deactivated' : 'activated'}`);
      loadTests();
    } catch (e) {
      toast.error("Failed to toggle test status");
    }
  };

  const copyTestLink = (testId) => {
    const link = `${window.location.origin}/test/${testId}`;
    navigator.clipboard.writeText(link);
    setCopiedTestId(testId);
    toast.success("Test link copied to clipboard!");
    setTimeout(() => setCopiedTestId(null), 2000);
  };

  const handleStatusChange = async (id, action) => {
    try {
      if (action === 'test') await startTestingMode(id);
      else if (action === 'publish') await publishTest(id);
      else if (action === 'unpublish') await unpublishTest(id);
      toast.success(`Test ${action === 'publish' ? 'published' : action === 'test' ? 'moved to testing' : 'unpublished'}`);
      loadTests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change status");
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <AdminSideNav active="Tests" />
      <div className="flex-1 flex flex-col">
        <BrandedHeader className="lg:hidden">
          <h1 className="text-lg font-display font-semibold text-center">Screening Tests</h1>
        </BrandedHeader>
        <div className="px-4 sm:px-6 lg:px-8 py-6 flex-1">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-display font-semibold">Screening Tests</h1>
                <p className="text-muted-foreground text-sm mt-1">Create tests with AI or manually, preview, and publish</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => navigate('/admin/tests/ai-generate')} className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 text-white">
                  <Sparkles className="w-4 h-4 mr-2" /> AI Generator
                </Button>
                <Button onClick={() => navigate('/admin/tests/new')} className="btn-press" variant="outline" data-testid="create-test-button">
                  <Plus className="w-4 h-4 mr-2" /> Manual
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
            ) : tests.length === 0 ? (
              <div className="text-center py-20">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No screening tests yet</p>
                <Button onClick={() => navigate('/admin/tests/new')} variant="secondary" className="mt-4"><Plus className="w-4 h-4 mr-2" /> Create Your First Test</Button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="tests-list">
                {tests.map(test => {
                  const sc = statusConfig[test.status] || statusConfig.draft;
                  return (
                    <Card key={test.id} className="border-border bg-card card-hover">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-display font-semibold text-lg truncate">{test.title}</h3>
                              <Badge variant="outline" className={`text-xs ${sc.color}`}>{sc.label}</Badge>
                              <Badge variant="outline" className={`text-xs ${test.is_active !== false ? 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/25' : 'bg-secondary text-muted-foreground'}`}>
                                {test.is_active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                              {test.category && <Badge variant="secondary" className="text-xs">{test.category}</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-1 mb-2">{test.description}</p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>{test.questions?.length || 0} questions</span>
                              <span>{test.time_limit_minutes || 30} min</span>
                              <span>Pass: {test.passing_score || 70}%</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {/* Active/Inactive toggle (super admin only) */}
                            {isSuperAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className={`h-8 w-8 ${test.is_active !== false ? 'text-[hsl(var(--success))]' : 'text-muted-foreground'}`}
                                onClick={() => handleToggleActive(test)}
                                title={test.is_active !== false ? 'Deactivate test' : 'Activate test'}
                                data-testid="toggle-test-status"
                              >
                                {test.is_active !== false ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                              </Button>
                            )}
                            {/* Status action buttons */}
                            {test.status === 'draft' && (
                              <Button variant="outline" size="sm" onClick={() => handleStatusChange(test.id, 'test')} title="Move to Testing">
                                <Play className="w-3.5 h-3.5 mr-1" /> Test
                              </Button>
                            )}
                            {test.status === 'testing' && (
                              <>
                                <Button variant="outline" size="sm" onClick={() => navigate(`/admin/tests/${test.id}/preview`)} title="Preview Test">
                                  <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                                </Button>
                                <Button variant="default" size="sm" onClick={() => handleStatusChange(test.id, 'publish')} title="Publish">
                                  <Rocket className="w-3.5 h-3.5 mr-1" /> Publish
                                </Button>
                              </>
                            )}
                            {test.status === 'published' && (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => copyTestLink(test.id)}
                                  title="Copy shareable test link"
                                  data-testid="copy-test-link"
                                >
                                  {copiedTestId === test.id ? (
                                    <>
                                      <Check className="w-3.5 h-3.5 mr-1 text-[hsl(var(--success))]" />
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <Link2 className="w-3.5 h-3.5 mr-1" />
                                      Copy Link
                                    </>
                                  )}
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => navigate(`/admin/tests/${test.id}/candidates`)} title="Manage Candidates">
                                  <Users className="w-3.5 h-3.5 mr-1" /> Candidates
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleStatusChange(test.id, 'unpublish')} title="Unpublish">
                                  <RotateCcw className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/admin/tests/${test.id}/edit`)}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--destructive))]" onClick={() => handleDelete(test.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
